/**
 * Ruimtelijke navigatie met pijltjestoetsen (en tv-afstandsbedieningen die
 * pijltjes sturen). Hetzelfde model als de tvOS focus engine: focus springt naar
 * het dichtstbijzijnde element in de gekozen richting.
 *
 * Elementen doen mee als ze focusbaar zijn (button, a[href], input) of data-focusable hebben.
 * Een container met data-focus-group onthoudt welk kind het laatst focus had.
 */

type Dir = 'up' | 'down' | 'left' | 'right';

const KEYS: Record<string, Dir> = {
  ArrowUp: 'up',
  ArrowDown: 'down',
  ArrowLeft: 'left',
  ArrowRight: 'right',
};

const SELECTOR = 'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea, [data-focusable]';

function visible(el: HTMLElement): boolean {
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0 && getComputedStyle(el).visibility !== 'hidden';
}

function candidates(): HTMLElement[] {
  return [...document.querySelectorAll<HTMLElement>(SELECTOR)].filter(visible);
}

function score(from: DOMRect, to: DOMRect, dir: Dir): number | undefined {
  const fx = from.left + from.width / 2;
  const fy = from.top + from.height / 2;
  const tx = to.left + to.width / 2;
  const ty = to.top + to.height / 2;

  let main: number; // afstand in de looprichting
  let cross: number; // afwijking haaks daarop
  switch (dir) {
    case 'up':
      main = from.top - to.bottom;
      cross = Math.abs(fx - tx);
      break;
    case 'down':
      main = to.top - from.bottom;
      cross = Math.abs(fx - tx);
      break;
    case 'left':
      main = from.left - to.right;
      cross = Math.abs(fy - ty);
      break;
    case 'right':
      main = to.left - from.right;
      cross = Math.abs(fy - ty);
      break;
  }
  if (main < -4) return undefined; // ligt niet in die richting
  // Haakse afwijking zwaarder laten wegen: rijen en kolommen blijven "recht".
  return Math.max(main, 0) + cross * 2.5;
}

function move(dir: Dir): boolean {
  const current = document.activeElement as HTMLElement | null;
  const all = candidates();
  if (!current || current === document.body || !all.includes(current)) {
    const first = document.querySelector<HTMLElement>('[data-autofocus]') ?? all[0];
    first?.focus();
    return Boolean(first);
  }

  const from = current.getBoundingClientRect();
  let best: HTMLElement | undefined;
  let bestScore = Infinity;
  for (const el of all) {
    if (el === current) continue;
    const s = score(from, el.getBoundingClientRect(), dir);
    if (s !== undefined && s < bestScore) {
      bestScore = s;
      best = el;
    }
  }
  if (!best) return false;

  // Bij het binnenkomen van een andere groep: terug naar het laatst gefocuste kind.
  const group = best.closest<HTMLElement>('[data-focus-group]');
  if (group && !group.contains(current)) {
    const remembered = group.querySelector<HTMLElement>('[data-last-focus]');
    if (remembered && visible(remembered)) best = remembered;
  }

  best.focus({ preventScroll: true });
  best.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
  return true;
}

export function installSpatialNavigation(onBack: () => void): () => void {
  const onKey = (e: KeyboardEvent) => {
    const target = e.target as HTMLElement | null;
    // In tekstvelden links/rechts laten werken; keuzelijsten (select) gebruiken alle pijltjes zelf
    const typing =
      (target?.tagName === 'INPUT' && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) ||
      target?.tagName === 'SELECT' ||
      target?.tagName === 'TEXTAREA';
    const dir = KEYS[e.key];
    if (dir && !typing) {
      if (move(dir)) e.preventDefault();
      return;
    }
    // Back op tv-afstandsbedieningen komt vaak binnen als Escape of Backspace
    if (e.key === 'Escape' || (e.key === 'Backspace' && target?.tagName !== 'INPUT')) {
      e.preventDefault();
      onBack();
    }
  };

  const onFocus = (e: FocusEvent) => {
    const el = e.target as HTMLElement;
    const group = el.closest?.('[data-focus-group]');
    if (!group) return;
    group.querySelectorAll('[data-last-focus]').forEach((n) => n.removeAttribute('data-last-focus'));
    el.setAttribute('data-last-focus', '');
  };

  window.addEventListener('keydown', onKey);
  document.addEventListener('focusin', onFocus);
  return () => {
    window.removeEventListener('keydown', onKey);
    document.removeEventListener('focusin', onFocus);
  };
}
